import { createSlice } from "@reduxjs/toolkit";
import { toast } from 'react-toastify';
import { IAddressSliceState } from "../../utils/address.types";
import { ApiStatus } from "../../utils/api.types";
import { addAddress, deleteAddress, fetchAddresses, updateAddress, fetchAddressById } from "./addressThunk";

/**
 * Address slice initial state.
 */
const initialState: IAddressSliceState = {
    addresses: [],
    addressDetails: undefined,
    errorMessage: '',
    isAddressCreated: false,
    selectedAddressId: '',
    status: ApiStatus.IDLE
}

/**
 * Address Slice.
 */
const addressSlice = createSlice({
    name: 'address',
    initialState: initialState,
    reducers: {
        // Selected address id
        selectedAddressID: (state, action) => {
            state.selectedAddressId = action.payload || '';
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Addresses
            .addCase(fetchAddresses.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchAddresses.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.addresses = action.payload.addresses || [];
                state.errorMessage = '';
            })
            .addCase(fetchAddresses.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })

            // Fetch address by ID
            .addCase(fetchAddressById.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchAddressById.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.addressDetails = action.payload || undefined;
                state.errorMessage = '';
            })
            .addCase(fetchAddressById.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })

            // Add Address
            .addCase(addAddress.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(addAddress.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.isAddressCreated = true;
                state.addresses.push(action.payload.address);
                state.errorMessage = '';

                toast.success(action.payload.message);
            })
            .addCase(addAddress.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.isAddressCreated = false;
                state.errorMessage = action.error.message
            })

            // Delete Address
            .addCase(deleteAddress.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(deleteAddress.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.errorMessage = '';

                const { id, message } = action.payload;
                state.addresses = state.addresses.filter(item => item._id !== id)

                toast.success(message);
            })
            .addCase(deleteAddress.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message

                toast.success(state.errorMessage);
            })

            // Update Address
            .addCase(updateAddress.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(updateAddress.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                const { message, address: updatedAddress } = action.payload
                state.addresses = state.addresses
                    .map(item => (item._id === item._id ? { ...item, ...updateAddress } : item));

                state.errorMessage = '';

                toast.success(message);
            })
            .addCase(updateAddress.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
            })
    }
});

export default addressSlice.reducer;
